import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { sentryService } from './services/sentryService';
import { monitoringService } from './services/monitoringService';

// Initialize monitoring
sentryService.initialize();
monitoringService.trackUserAction('app_initialized', 'app');

// Create a simple theme context since the file is missing
const ThemeContext = React.createContext({
  isDarkMode: false,
  toggleTheme: () => {},
});

const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => !prev);
    monitoringService.trackUserAction('theme_toggled', 'app', undefined, { isDarkMode: !isDarkMode });
  }, [isDarkMode]);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
import { useAuth } from './hooks/useAuth';
import { refreshAuthSessionCompat } from './config/cognitoAuth';
import { userProfileGet, userProfilePatch, userRegisterProfile } from './services/tradingDbApi';
// Define client levels as a constant in this file for now
const CLIENT_LEVELS = [
  { level: 1, price: 4.99, name: 'Starter' },
  { level: 2, price: 9.99, name: 'Intermediate' },
  { level: 3, price: 19.99, name: 'Advanced' },
  { level: 4, price: 39.99, name: 'Professional' },
  { level: 5, price: 79.99, name: 'Expert' },
];
import TermsSummaryModal from './components/TermsSummaryModal';

// Type definitions
interface UserData {
  email: string;
  level: number;
  fullName: string;
  phone: string;
  payment_status?: 'pending' | 'completed' | 'failed';
}


// Components
import Header from './components/Header';
import Footer from './components/Footer';
import {
  LazyEnterpriseLandingPage,
  LazySuccess,
  LazyDashboard,
  LazyAdminDashboard,
  LazyLevels,
  LazyPayment,
  LazyRegistrationForm,
  LazyTermsAndConditions,
  LazyPrivacyPolicy,
  LazyRefundsAndCancellations,
  LazyContactUs,
  LazyServices,
  LazyHealthCheck,
  LazyMonitoringDashboard
} from './components/LazyComponents';

// Import RegistrationForm component

declare module './components/Payment' {
  interface PaymentProps {
    userData: UserData;
    onPaymentSuccess: () => void;
    onBack: () => void;
  }
}

declare module './components/Success' {
  interface SuccessProps {
    userData: UserData;
    onContinue: () => void;
    onReset: () => void;
  }
}

// Styles
import './styles/globals.css';
import { isAdminEmail } from './config/adminAuth';
import { shouldApplyAuthBypass } from './config/environment';

type AppView = 'home' | 'levels' | 'registration' | 'payment' | 'success' | 'dashboard' | 'admin-dashboard' | 'reset-password' | 'register' | 'payment-test' | 'terms' | 'privacy' | 'refunds' | 'contact' | 'services' | 'health' | 'monitoring';

// Main App component with routing and theme provider
const App = () => {
  return (
    <React.StrictMode>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </React.StrictMode>
  );
};

// Separate component for app content to use hooks at the top level
const AppContent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<AppView>('home');
  const [navigationHistory, setNavigationHistory] = useState<string[]>(['home']);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [pendingLevel, setPendingLevel] = useState<number | null>(null);
  const [pendingFee, setPendingFee] = useState<number | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Simple navigation function
  const navigateToView = (newView: AppView) => {
    setView(newView);
  };

  // Listen for custom navigation events from header dropdowns
  useEffect(() => {
    const handleNavigateToView = (event: CustomEvent) => {
      const newView = event.detail.view as AppView;
      if (newView && newView !== view) {
        setView(newView);
      }
    };

    window.addEventListener('navigateToView', handleNavigateToView as EventListener);
    return () => {
      window.removeEventListener('navigateToView', handleNavigateToView as EventListener);
    };
  }, [view]);
  
  const fullTerms = `TERMS AND CONDITIONS - CRYPTO COPY TRADING SERVICE

1. ELIGIBILITY
You must be at least 18 years old and have a valid Binance Futures account with API trading access.

2. NATURE OF SERVICE
This is a non-advisory, execution-only service where trades are mirrored to your Binance Futures account via API access.

3. RISK DISCLOSURE
- Trading involves significant risk of loss
- Past performance is not indicative of future results
- You may lose some or all of your investment
- The strategy may use up to 20x leverage

4. USER RESPONSIBILITIES
- Maintain sufficient funds in your account
- Monitor your account regularly
- Keep your API credentials secure

5. TERMINATION
We reserve the right to terminate service for any violation of these terms.`;

  // Navigation handler
  const handleNavigate = useCallback((newView: AppView) => {
    setView(newView);
    navigate(`/${newView === 'home' ? '' : newView}`);
  }, [navigate]);

  // Handle user registration
  const handleRegistrationSuccess = useCallback(() => {
    // Proceed to payment after successful registration
    setView('payment');
  }, []);

  // Handle payment success
  const handlePaymentSuccess = useCallback(async () => {
    try {
      const patch = await userProfilePatch({
        email: user?.email,
        payment_status: 'completed',
        created_at: new Date().toISOString(),
      });
      if (!patch.ok) throw new Error(patch.error || 'profile update failed');
      const data = patch.data;

      await refreshAuthSessionCompat();

      // Update local state with the latest data
      setUserData(prev => ({
        ...prev!,
        ...(data || {}),
        payment_status: 'completed' as const
      }));
      
      // Navigate directly to dashboard
      setView('dashboard');
      
    } catch (error) {
      console.error('Error updating payment status:', error);
      // If there's an error, still try to go to dashboard
      setView('dashboard');
    }
  }, [user?.email]);

  // Handle payment back
  const handlePaymentBack = useCallback(() => {
    setView('levels');
  }, []);

  // Update navigation history when view changes
  useEffect(() => {
    // Don't add duplicate views to history
    if (navigationHistory[navigationHistory.length - 1] !== view) {
      setNavigationHistory(prev => [...prev, view]);
    }
  }, [view]);

  // Handle terms rejection
  const handleRejectTerms = useCallback(() => {
    setShowTermsModal(false);
    setPendingLevel(null);
    setPendingFee(null);
  }, []);
  
  // Handle terms agreement (combined accept with level selection)
  const handleAgreeToTerms = useCallback(async () => {
    if (!pendingLevel || !pendingFee) {
      console.error('Missing pending level or fee');
      return;
    }
    
    console.log('Agreeing to terms for level:', pendingLevel, 'fee:', pendingFee);
    setShowTermsModal(false);
    setSelectedLevel(pendingLevel);
    
    try {
      // If user is logged in, try to create or update their data
      if (user) {
        console.log('Creating/updating user data in database...');
        
        const existing = await userProfileGet(
          user.id,
          typeof user.email === 'string' ? user.email : undefined
        );
        const ts = new Date().toISOString();
        if (existing.ok && existing.data) {
          const upd = await userProfilePatch({
            user_id: user.id,
            ...(typeof user.email === 'string' ? { email: user.email } : {}),
            active_level: pendingLevel,
            payment_status: 'pending',
            created_at: ts,
          });
          if (upd.ok && upd.data) {
            console.log('User updated successfully:', upd.data);
            setUserData(prev => ({
              ...prev!,
              ...upd.data,
              level: pendingLevel,
              payment_status: 'pending' as const
            }));
          } else {
            console.log('Profile patch failed, using local state');
            setUserData(prev => ({
              ...prev!,
              id: user.id,
              email: user.email || '',
              fullName: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
              phone: user.user_metadata?.phone || '',
              level: pendingLevel,
              payment_status: 'pending' as const
            }));
          }
        } else {
          const ins = await userRegisterProfile({
            id: user.id,
            email: user.email,
            full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
            phone: user.user_metadata?.phone || '',
            active_level: pendingLevel,
            payment_status: 'pending',
            created_at: ts,
          });
          if (ins.ok && ins.data) {
            console.log('User created successfully:', ins.data);
            setUserData(prev => ({
              ...prev!,
              ...ins.data,
              level: pendingLevel,
              payment_status: 'pending' as const
            }));
          } else {
            console.log('Register profile failed, using local state:', ins.error);
            setUserData(prev => ({
              ...prev!,
              id: user.id,
              email: user.email || '',
              fullName: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
              phone: user.user_metadata?.phone || '',
              level: pendingLevel,
              payment_status: 'pending' as const
            }));
          }
        }
      } else {
        console.log('No user logged in, proceeding to payment without saving to DB');
      }
      
      // Navigate to payment page
      console.log('Navigating to payment page');
      setView('payment');
      
      await refreshAuthSessionCompat();
      
    } catch (error) {
      console.error('Error in handleAgreeToTerms:', error);
      // If there's an error, still try to proceed to payment
      setView('payment');
    }
  }, [pendingLevel, pendingFee, user]);


  // Handle auth state changes and routing
  useEffect(() => {
    const handleAuthStateChange = async () => {
      // If user is not logged in, ensure we're on the home page
      if (!user) {
        if (view !== 'home') {
          setView('home');
        }
        return;
      }

      // Check if we're on the password reset page
      const isPasswordResetPage = window.location.pathname === '/reset-password';
      if (isPasswordResetPage) {
        return;
      }

      const authBypass = shouldApplyAuthBypass();
      if (authBypass) {
        if (isAdminEmail(user.email)) {
          if (view !== 'admin-dashboard') {
            setView('admin-dashboard');
          }
          return;
        }
        if (view !== 'dashboard') {
          setView('dashboard');
        }
        return;
      }
      
      // If this is an admin user, always go directly to admin dashboard
      if (isAdminEmail(user.email)) {
        if (view !== 'admin-dashboard') {
          setView('admin-dashboard');
        }
        return;
      }
      
      try {
        const res = await userProfileGet(
          user.id,
          typeof user.email === 'string' ? user.email : undefined
        );
        const { ok, data, error } = res;
        const missingProfile = !ok && /not found/i.test(String(error || ''));

        if (ok && data) {
          const ps = (data.payment_status as 'pending' | 'completed' | 'failed' | undefined) || 'pending';
          // Update user data with payment status
          setUserData(prev => ({
            ...prev!,
            ...data,
            payment_status: ps,
          }));
          
          // Handle routing based on payment status
          if (data.payment_status === 'pending') {
            // If we're on payment or registration page, stay there
            if (view === 'payment' || view === 'registration') {
              return;
            }
            // If we just set the view to payment in handleAgreeToTerms, don't override it
            if (pendingLevel !== null) {
              return;
            }
            // Otherwise, go to levels page
            if (view !== 'levels') {
              setView('levels');
            }
          } else if (data.payment_status === 'completed') {
            // If payment is completed and we're not already on dashboard
            if (view !== 'dashboard' && view !== 'success') {
              setView('dashboard');
            }
          }
        } else if (missingProfile || !data) {
          // No user data found - this is a new user who just signed up
          console.log('New user detected, redirecting to levels page');
          // Don't override if user is already on payment page or in the middle of level selection
          if (view !== 'levels' && view !== 'payment' && pendingLevel === null) {
            setView('levels');
          }
        } else {
          throw new Error(error || 'profile fetch failed');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // If there's an error, redirect to home
        setView('home');
      }
    };
    
    handleAuthStateChange();
  }, [user, view, pendingLevel]);
  
  // Handle level selection with terms agreement
  const handleSelectLevel = useCallback((level: number) => {
    const levelObj = CLIENT_LEVELS.find(l => l.level === level);
    if (levelObj) {
      setPendingLevel(level);
      setPendingFee(levelObj.price);
      // Always show terms modal when a level is selected
      setShowTermsModal(true);
    }
  }, []);


  // Render the appropriate content based on the current view
  const renderContent = () => {
    switch (view) {
      case 'home':
        return <LazyEnterpriseLandingPage />;
      case 'levels':
        return <LazyLevels onSelectLevel={handleSelectLevel} />;
      case 'registration':
        return selectedLevel && pendingFee ? (
          <LazyRegistrationForm 
            level={selectedLevel} 
            fee={pendingFee} 
            onBack={() => setView('levels')} 
            onSuccess={handleRegistrationSuccess} 
          />
        ) : (
          <Navigate to="/levels" replace />
        );
      case 'payment':
        return userData ? (
          <LazyPayment 
            userData={userData} 
            onPaymentSuccess={handlePaymentSuccess}
            onBack={handlePaymentBack}
          />
        ) : (
          <div className="text-center p-8">
            <p>No user data available. Please complete the registration first.</p>
            <button 
              onClick={() => handleNavigate('register')}
              className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
            >
              Back to Registration
            </button>
          </div>
        );
      case 'success':
        if (!userData) return <Navigate to="/" replace />;
        return (
          <LazySuccess 
            userData={userData} 
            onReset={() => {
              setView('home');
              setUserData(null);
            }}
            onContinue={() => {
              setView('dashboard');
              setUserData(null);
            }} 
          />
        );
      case 'dashboard':
        return <LazyDashboard />;
      case 'admin-dashboard':
        return isAdminEmail(user?.email) ? <LazyAdminDashboard /> : <Navigate to="/" replace />;
      case 'terms':
        return <LazyTermsAndConditions onNavigate={navigateToView} />;
      case 'privacy':
        return <LazyPrivacyPolicy onNavigate={navigateToView} />;
      case 'refunds':
        return <LazyRefundsAndCancellations onNavigate={navigateToView} />;
      case 'contact':
        return <LazyContactUs onNavigate={navigateToView} />;
      case 'services':
        return <LazyServices onNavigate={navigateToView} />;
      case 'health':
        return <LazyHealthCheck />;
      case 'monitoring':
        return <LazyMonitoringDashboard />;
      default:
        return <Navigate to="/" replace />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Header />
      <main className="flex-1 w-full">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {renderContent()}
        </div>
      </main>
      {/* Footer is now rendered in the main layout only */}
      {view !== 'dashboard' && view !== 'admin-dashboard' && <Footer />}
      {showTermsModal && pendingLevel !== null && pendingFee !== null && (
        <TermsSummaryModal
          open={showTermsModal}
          onAgree={handleAgreeToTerms}
          onClose={handleRejectTerms}
          level={pendingLevel}
          fee={pendingFee}
          fullTerms={fullTerms}
        />
      )}
    </div>
  );
};

export default App;